<template>
  <ts-modal
    ref="modal"
    :modal-title="modalTitle"
    modal-class="modal-form candidate-event-modal"
    :keyboard="false"
    @modal-shown="onModalShown"
  >
    <template #modal-content>
      <div ref="modalBody">
        <alert
          :type="errorType ?? undefined"
          :message="errorMessage ?? undefined"
          :details-h-t-m-l="errorMessageDetails ?? undefined"
          @dismiss="clearError"
        >
          <template v-if="errorTeam">
            <a
              :href="selectedEvent.eventTeamUrl ?? undefined"
              class="btn btn-info btn-sm"
              target="_blank"
            >
              Go to teams page
            </a>
          </template>
        </alert>
        <div class="add-event">
          <div class="row">
            <div class="col">
              <div class="mb-2">
                <label class="form-label">Event <span class="text-danger">*</span></label>
                <select
                  v-if="isNewEvent"
                  ref="newEvent"
                  class="form-select form-select-sm autocomplete"
                  :data-autocomplete-url="autocompleteUrl"
                />
                <input
                  v-else
                  :value="selectedEvent.eventName"
                  type="text"
                  readonly
                  class="form-control form-control-sm"
                >
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <div class="mb-2">
                <label
                  for="priority"
                  class="form-label"
                >Priority <span
                  v-if="priorityEnabled"
                  class="text-danger"
                >*</span></label>
                <input
                  id="priority"
                  v-model="selectedEvent.priority"
                  type="text"
                  :disabled="!priorityEnabled"
                  class="form-control form-control-sm"
                >
                <small class="form-text text-muted">Enter numerical value, "RESERVE" or "RESERVE X" where X is a number</small>
              </div>
            </div>
            <div class="col">
              <div class="mb-2">
                <label
                  for="language"
                  class="form-label"
                >Language <span
                  v-if="languageEnabled"
                  class="text-danger"
                >
                  *
                </span></label>
                <select
                  id="language"
                  v-model="selectedEvent.languageId"
                  :disabled="!languageEnabled"
                  class="form-select form-select-sm"
                >
                  <option />
                  <option
                    v-for="language in selectedEvent.eventLanguages"
                    :key="language.id"
                    :value="language.id"
                  >
                    {{ language.title }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div
            v-if="showStatusStr"
            class="row"
          >
            <div class="col">
              <div class="mb-2">
                <label class="form-label">Status</label>
                <input
                  id="staticEmail"
                  type="text"
                  readonly
                  class="form-control form-control-sm"
                  :value="selectedEvent.statusStr"
                >
              </div>
            </div>
          </div>
          <div
            v-if="showModifiedAt"
            class="text-end"
          >
            <small class="text-muted">Last modified at: {{ selectedEvent.modifiedAtStr }}</small>
          </div>
          <loading-indicator
            v-if="isLoading"
          />
        </div>
      </div>
    </template>
    <template #modal-footer>
      <button
        v-if="showDelete"
        type="button"
        class="btn btn-sm btn-danger"
        @click="deleteEvent"
      >
        {{ deleteTitle }}
      </button>
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
      >
        Close
      </button>
      <button
        v-if="canModify"
        type="button"
        :disabled="!canSave"
        class="btn btn-sm btn-primary"
        @click="updateEvent"
      >
        Ok
      </button>
    </template>
  </ts-modal>
  <teleport
    to="#vue-portal-target"
    append
  >
    <cancel-participation-modal
      ref="cancelParticipationModal"
      :cancellation-reasons="cancellationReasons"
      @participation-canceled="participationCanceled"
    />
  </teleport>
</template>

<script setup lang="ts">
import {computed, nextTick, ref, useTemplateRef} from 'vue'
import $ from 'jquery'
import axios from 'axios'
import Rails from '@rails/ujs'

import Alert from './alert.vue'
import LoadingIndicator from './loading-indicator.vue'
import CancelParticipationModal from './cancel-participation-modal.vue'
import TsModal from './ts-modal.vue'
import { EventCandidateStatus } from './../event-candidate-status'
import { initializeFormFields } from '../site-utils'
import { isNonEmptyString } from '../js-utils'
import type { CancellationReason } from './types'

interface EventLanguage {
  id: number
  title: string
}

interface SelectedEvent {
  id: number | null
  eventId: number | string | null
  eventName: string | null
  eventTeamUrl: string | null
  eventLanguages: EventLanguage[]
  priority: string | null
  languageId: number | string | null
  modifiedAtStr: string | null
  candidateId: number | null
  editable: boolean
  statusStr: string | null
  statusId: number
  canCancel: boolean
  canDelete: boolean
}

interface CandidateEvent extends Partial<SelectedEvent> {
  eventId: number | string | null
  eventName: string | null
}

interface Select2EventData {
  id: number | string
  text: string
  languages?: EventLanguage[]
  teamUrl?: string | null
}

const props = defineProps({
  autocompleteUrl: { default: null, type: String },
  modalTitle: { default: '', type: String },
  saveUrl: { default: null, type: String },
  cancellationReasons: { type: Array as () => CancellationReason[], default: (): CancellationReason[] => [] }
})

const emit = defineEmits<{
  update: [event: SelectedEvent]
  cancel: [event: SelectedEvent, reasonId?: string, comment?: string]
}>()

const modal = useTemplateRef<InstanceType<typeof TsModal>>('modal')
const modalBody = useTemplateRef<HTMLElement>('modalBody')
const newEvent = useTemplateRef<HTMLSelectElement>('newEvent')
const cancelParticipationModal = useTemplateRef<InstanceType<typeof CancelParticipationModal>>('cancelParticipationModal')

const createDefaultSelectedEvent = (): SelectedEvent => ({
  id: null,
  eventId: null,
  eventName: null,
  eventTeamUrl: null,
  eventLanguages: [],
  priority: null,
  languageId: null,
  modifiedAtStr: null,
  candidateId: null,
  editable: true,
  statusStr: null,
  statusId: EventCandidateStatus.new,
  canCancel: false,
  canDelete: false,
})

const errorType = ref<'danger' | 'info' | null>(null)
const errorMessage = ref<string | null>(null)
const errorMessageDetails = ref<string | null>(null)
const errorTeam = ref(false)
const isNewEvent = ref(true)
const isLoading = ref(false)
const selectedEvent = ref<SelectedEvent>(createDefaultSelectedEvent())

const canSave = computed((): boolean => {
  return selectedEvent.value.statusId !== EventCandidateStatus.canceled &&
    selectedEvent.value.statusId !== EventCandidateStatus.deleted && !isLoading.value &&
    selectedEvent.value.editable
})

const canModify = computed((): boolean => {
  return selectedEvent.value.editable === true
})

const showDelete = computed((): boolean => {
  return !isNewEvent.value && canModify.value && (selectedEvent.value.canCancel || selectedEvent.value.canDelete)
})

const deleteTitle = computed((): string | null => {
  if (selectedEvent.value.canCancel) {
    if (selectedEvent.value.statusId === EventCandidateStatus.canceled) {
      return 'Undo cancellation'
    }
    return 'Cancel participation'
  }
  if (selectedEvent.value.canDelete) {
    if (selectedEvent.value.statusId === EventCandidateStatus.deleted) {
      return 'Undo removal'
    }
    return 'Remove from event'
  }
  return null
})

const hasEvent = computed((): boolean => {
  let eventId = null
  if (typeof selectedEvent.value.eventId == "string") {
    eventId = parseInt(selectedEvent.value.eventId)
  } else if (typeof selectedEvent.value.eventId == "number") {
    eventId = selectedEvent.value.eventId
  } else {
    return false
  }
  return !isNaN(eventId) && eventId > 0
})

const requireTeam = computed((): boolean => {
  return isNonEmptyString(selectedEvent.value.eventTeamUrl)
})

const requireLanguage = computed((): boolean => {
  return Array.isArray(selectedEvent.value.eventLanguages) && selectedEvent.value.eventLanguages.length > 0
})

const priorityEnabled = computed((): boolean => {
  return canSave.value && hasEvent.value && !requireTeam.value
})

const languageEnabled = computed((): boolean => {
  return canSave.value && priorityEnabled.value && requireLanguage.value
})

const showModifiedAt = computed((): boolean => {
  return isNonEmptyString(selectedEvent.value.modifiedAtStr)
})

const showStatusStr = computed((): boolean => {
  return isNonEmptyString(selectedEvent.value.statusStr)
})

const clearError = (): void => {
  errorType.value = 'danger'
  errorMessage.value = null
  errorMessageDetails.value = null
  errorTeam.value = false
}

const setErrorMessage = (message: string): void => {
  errorMessage.value = message
}

const resetEvent = (): void => {
  selectedEvent.value = createDefaultSelectedEvent()
}

const attachNewEventHandlers = (): void => {
  if (!newEvent.value) {
    return
  }

  $(newEvent.value)
    .off('select2:select.candidateEventModal select2:unselect.candidateEventModal')
    .on('select2:select.candidateEventModal', (e) => {
      const eventData = (e as JQuery.TriggeredEvent & { params: { data: Select2EventData } }).params.data
      selectedEvent.value = {
        id: null,
        eventId: eventData.id,
        eventName: eventData.text,
        eventLanguages: Array.isArray(eventData.languages) ? eventData.languages : [],
        eventTeamUrl: eventData.teamUrl ?? null,
        priority: null,
        languageId: null,
        modifiedAtStr: null,
        candidateId: null,
        editable: true,
        statusStr: null,
        statusId: EventCandidateStatus.new,
        canCancel: false,
        canDelete: false
      }
      clearError()
      showTeamMessage()
    })
    .on('select2:unselect.candidateEventModal', () => {
      resetEvent()
      clearError()
    })
}

const initializeModalFields = (): void => {
  // FIXME: there should be a better way,
  // but seems that autocompleteUrl binding does not
  // happen instantaneously
  nextTick(() => {
    nextTick(() => {
      if (modalBody.value) {
        initializeFormFields(modalBody.value)
      }
      attachNewEventHandlers()
    })
  })
}

const showModal = (candidateEvent: CandidateEvent | null): void => {
  clearError()
  if (candidateEvent) {
    isNewEvent.value = false
    resetEvent()
    selectedEvent.value.id = candidateEvent.id ?? null
    selectedEvent.value.eventId = candidateEvent.eventId
    selectedEvent.value.eventName = candidateEvent.eventName
    selectedEvent.value.eventTeamUrl = candidateEvent.eventTeamUrl ?? null
    selectedEvent.value.eventLanguages = Array.isArray(candidateEvent.eventLanguages) ? candidateEvent.eventLanguages : []
    selectedEvent.value.priority = candidateEvent.priority ?? null
    selectedEvent.value.languageId = candidateEvent.languageId ?? null
    selectedEvent.value.modifiedAtStr = candidateEvent.modifiedAtStr ?? null
    selectedEvent.value.candidateId = candidateEvent.candidateId ?? null
    selectedEvent.value.statusStr = candidateEvent.statusStr ?? null
    selectedEvent.value.statusId = candidateEvent.statusId ?? EventCandidateStatus.new
    selectedEvent.value.editable = candidateEvent.editable ?? true
    selectedEvent.value.canCancel = candidateEvent.canCancel === true
    selectedEvent.value.canDelete = isNewEvent.value || candidateEvent.canDelete === true
  } else {
    isNewEvent.value = true
    resetEvent()
  }

  nextTick(() => {
    initializeModalFields()
    modal.value?.show()
  })
}

const hideModal = (): void => {
  modal.value?.hide()
}

const onModalShown = (): void => {
  if (isNewEvent.value && newEvent.value) {
    $(newEvent.value).select2('open')
  }
}

const showTeamMessage = (): boolean => {
  if (requireTeam.value) {
    errorType.value = 'info'
    errorMessage.value = 'Teams for this event must be managed through event details page'
    errorTeam.value = true
    return true
  }
  return false
}

const languageIsValid = (): boolean => {
  if (!requireLanguage.value) {
    return true
  }

  for (const lang of selectedEvent.value.eventLanguages) {
    if (lang.id === selectedEvent.value.languageId) {
      return true
    }
  }
  errorMessage.value = 'Please select language'
  return false
}

const priorityIsValid = (): boolean => {
  selectedEvent.value.priority = String(selectedEvent.value.priority).replace(/['"]/g, '')
  const pValue = selectedEvent.value.priority
  const iValue = parseInt(pValue)
  if (!isNaN(iValue)) {
    if (iValue < 0) {
      errorMessage.value = 'Invalid priority value. Enter positive number or \'RESERVE\''
      return false
    }
    return true
  } else {
    const val = String(pValue).trim().toUpperCase()
    if (/^RESERVE\s*(\d+)*\s*$/.test(val)) {
      return true
    }
  }
  errorMessage.value = 'Invalid priority value. Enter positive number or \'RESERVE\''
  return false
}

const updateEvent = (): void => {
  clearError()
  if (!hasEvent.value) {
    errorMessage.value = 'Please select an event'
    return
  }

  if (showTeamMessage()) {
    return
  }

  if (!priorityIsValid()) {
    return
  }

  if (requireLanguage.value && !languageIsValid()) {
    return
  }

  if (typeof props.saveUrl === 'string' && props.saveUrl.length > 0) {
    isLoading.value = true
    axios.post(props.saveUrl, {
      event_candidate: {
        candidate_id: selectedEvent.value.candidateId,
        priority_str: selectedEvent.value.priority,
        event_language_id: selectedEvent.value.languageId
      }
    }, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': Rails.csrfToken(),
        'Accept': 'application/json'
      }
    })
      .then((response) => {
        if (typeof response.data.error === 'string') {
          errorMessage.value = response.data.error
          errorMessageDetails.value = response.data.error_details
          isLoading.value = false
        } else {
          window.location.href = window.location.href
        }
        // if (typeof response.data.message === 'string') {
        //   if (response.data.success) {
        //     window.location.href = registrationUrl
        //   } else {
        //     errorMessage.value = response.data.message
        //     isLoading.value = false
        //   }
        // } else {
        //   errorMessage.value = 'Unknown response from server'
        //   isLoading.value = false
        // }
      })
      .catch((error) => {
        errorMessage.value = error.message
        isLoading.value = false
      })
  } else {
    emit('update', selectedEvent.value)
  }
}

const showCancelParticipationModal = (): void => {
  cancelParticipationModal.value?.showModal()
}

const hideCancelParticipationModal = (): void => {
  cancelParticipationModal.value?.hideModal()
}

const deleteEvent = (): void => {
  clearError()
  if (selectedEvent.value.canCancel) {
    if (selectedEvent.value.statusId === EventCandidateStatus.canceled) {
      if (confirm('Undo participation cancellation?')) {
        selectedEvent.value.statusId = EventCandidateStatus.selected
      }
    } else if (selectedEvent.value.statusId === EventCandidateStatus.selected) {
      showCancelParticipationModal()
    }
  } else if (selectedEvent.value.canDelete) {
    if (selectedEvent.value.statusId === EventCandidateStatus.deleted) {
      if (confirm('Restore participant to event?')) {
        selectedEvent.value.statusId = EventCandidateStatus.pending
      }
    } else if (selectedEvent.value.statusId === EventCandidateStatus.new || selectedEvent.value.statusId === EventCandidateStatus.pending) {
      showCancelParticipationModal()
    }
  }
}

const participationCanceled = (reasonId?: string, comment?: string): void => {
  hideCancelParticipationModal()
  emit('cancel', selectedEvent.value, reasonId, comment)
}

defineExpose({
  hideModal,
  setErrorMessage,
  showModal,
})
</script>
